import type {
  AppointmentTypeDefaultsSource,
  ScheduledItemSummary,
  TypeDefaults,
} from './TypeDefaultsPreview.types';

const MODALITY_LABELS: Record<string, string> = {
  in_person: 'In-person',
  video: 'Video',
  audio: 'Audio',
};

/** Common CMS place-of-service codes surfaced by the session-defaults model. */
const PLACE_OF_SERVICE_LABELS: Record<string, string> = {
  '02': 'Telehealth',
  '10': "Telehealth — patient's home",
  '11': 'Office',
};

const ISO_DURATION_PATTERN =
  /^(-)?P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/i;

const pluralize = (count: number, unit: string): string =>
  `${count} ${unit}${count === 1 ? '' : 's'}`;

/**
 * "-P1D" -> "1 day before" · "-PT2H" -> "2 hours before" · "P0D" -> "at visit
 * time". Returns undefined for unparseable values (caller omits the timing).
 */
export const formatRuleOffset = (offset: string): string | undefined => {
  const match = ISO_DURATION_PATTERN.exec(offset ?? '');
  if (!match) return undefined;
  const [, sign, weeks, days, hours, minutes] = match;

  const parts: string[] = [];
  if (weeks && Number(weeks) > 0) parts.push(pluralize(Number(weeks), 'week'));
  if (days && Number(days) > 0) parts.push(pluralize(Number(days), 'day'));
  if (hours && Number(hours) > 0) parts.push(pluralize(Number(hours), 'hour'));
  if (minutes && Number(minutes) > 0)
    parts.push(pluralize(Number(minutes), 'minute'));

  if (parts.length === 0) return 'at visit time';
  return `${parts.join(' ')} ${sign ? 'before' : 'after'}`;
};

export const formatPlaceOfService = (code: string): string => {
  const label = PLACE_OF_SERVICE_LABELS[code];
  return label ? `${code} · ${label}` : code;
};

export const formatModality = (
  modality: 'in_person' | 'video' | 'audio',
  useJotmeet?: boolean | null
): string => {
  const label = MODALITY_LABELS[modality] ?? modality;
  return useJotmeet && modality === 'video' ? `${label} · JotMeet` : label;
};

const mapRules = (
  rules: Array<{ title?: string | null; offset: string }> | null | undefined,
  fallbackTitle: string
): ScheduledItemSummary[] =>
  (rules ?? []).map((rule) => ({
    title: rule.title?.trim() || fallbackTitle,
    timing: formatRuleOffset(rule.offset),
  }));

/**
 * Number of default fields a type actually sets — used by consumers to
 * decide whether the preview earns screen space (e.g. the picker only shows
 * the chips row for types with >= 2 set fields) and for analytics metadata.
 */
export const countSetDefaults = (defaults: TypeDefaults): number =>
  [
    defaults.templateSet,
    defaults.durationLabel !== null,
    defaults.modalityLabel !== null,
    defaults.placeOfServiceLabel !== null,
    defaults.cptCodes.length > 0,
    defaults.forms.length > 0,
    defaults.reminders.length > 0,
  ].filter(Boolean).length;

/**
 * Map an appointment type's raw defaults (generated API shape) to the
 * display-ready TypeDefaults the preview renders. Template NAME is not on
 * the type payload — pass it via options once resolved (useTemplateNameById);
 * until then the preview shows a "template selected" placeholder.
 */
export const mapAppointmentTypeToDefaults = (
  source: AppointmentTypeDefaultsSource,
  options?: { templateName?: string | null }
): TypeDefaults => ({
  templateName: options?.templateName ?? null,
  templateSet: Boolean(source.template_id),
  durationLabel:
    source.default_duration != null ? `${source.default_duration} min` : null,
  modalityLabel: source.default_modality
    ? formatModality(source.default_modality, source.default_use_jotmeet)
    : null,
  placeOfServiceLabel: source.default_place_of_service
    ? formatPlaceOfService(source.default_place_of_service)
    : null,
  placeOfServiceCode: source.default_place_of_service ?? null,
  cptCodes: source.default_cpt_codes ?? [],
  forms: mapRules(source.default_form_rules, 'Form'),
  reminders: mapRules(source.default_reminder_rules, 'Reminder'),
});
